import type { AttributeInput } from '__generated__/graphql'
import type { ConsentState } from 'lib/consent'

// Cart attributes that Shopify copies onto the order as note_attributes. The
// GA4 webhook reads the _ga_* ids, click ids and consent; the _utm_* keys show
// each order's campaign under Additional details, including for shoppers whose
// browser blocks GA or who declined cookies.

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
]
const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid']

// Describe the landing that led to this checkout, so they're replaced as a set:
// a stale utm_content from an earlier checkout must not mix into a new campaign.
const isCampaignKey = (key: string) =>
  key.startsWith('_utm_') || CLICK_ID_KEYS.some((id) => key === `_${id}`)

type ExistingAttributes = ReadonlyArray<{ key: string; value?: string | null }>

// JSON-encoded pairs, so a value containing & or = can't pass for two pairs.
const toEntries = (attributes: ExistingAttributes) =>
  JSON.stringify(
    attributes
      .filter(({ value }) => !!value)
      .map(({ key, value }) => JSON.stringify([key, value]))
      .sort()
  )

export const sameAttributes = (
  existing: ExistingAttributes,
  next: AttributeInput[]
) => toEntries(existing) === toEntries(next)

interface Attribution {
  clientId: string | undefined
  sessionId: string | undefined
  campaignParams: Record<string, string>
  marketingConsent: ConsentState
}

// cartAttributesUpdate replaces every attribute, so merge onto the cart's
// current ones. GA ids keep their earlier values when this write couldn't read
// them (e.g. GA blocked), so a retry never wipes a client_id. Consent reflects
// now, except an unreadable answer keeps an earlier denial (never a grant).
export const buildCartAttribution = (
  existing: ExistingAttributes,
  { clientId, sessionId, campaignParams, marketingConsent }: Attribution
): AttributeInput[] => {
  const replaced = new Set<string>()
  if (clientId) replaced.add('_ga_client_id').add('_ga_session_id')

  const keepConsent = ({ value }: { value?: string | null }) =>
    !marketingConsent && value === 'denied'

  const kept = existing.filter(
    (attr) =>
      !replaced.has(attr.key) &&
      !isCampaignKey(attr.key) &&
      (attr.key !== '_marketing_consent' || keepConsent(attr))
  )

  // Only known keys: Shopify rejects the whole write past 250 attributes.
  const campaign = Object.entries(campaignParams)
    .filter(([key]) => UTM_KEYS.includes(key) || CLICK_ID_KEYS.includes(key))
    .map(([key, value]) => ({ key: `_${key}`, value }))

  return [
    ...kept,
    { key: '_ga_client_id', value: clientId },
    { key: '_ga_session_id', value: clientId ? sessionId : undefined },
    { key: '_marketing_consent', value: marketingConsent },
    ...campaign,
  ].filter((attr): attr is AttributeInput => !!attr.value)
}
